import { baseApiClient as api } from '../baseService';

export const addTagTypes = ['cms-feature-config'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      firmsCmsFeatureConfigsList: build.query<
        FirmsCmsFeatureConfigsListApiResponse,
        FirmsCmsFeatureConfigsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/`,
          params: {
            cms_type: queryArg.cmsType,
          },
        }),
        providesTags: ['cms-feature-config'],
      }),
      firmsCmsFeatureConfigsCreate: build.mutation<
        FirmsCmsFeatureConfigsCreateApiResponse,
        FirmsCmsFeatureConfigsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/`,
          method: 'POST',
          body: queryArg.createCmsFeatureConfigSchema,
        }),
        invalidatesTags: ['cms-feature-config'],
      }),
      firmsCmsFeatureConfigsRetrieve: build.query<
        FirmsCmsFeatureConfigsRetrieveApiResponse,
        FirmsCmsFeatureConfigsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/`,
        }),
        providesTags: ['cms-feature-config'],
      }),
      firmsCmsFeatureConfigsPartialUpdate: build.mutation<
        FirmsCmsFeatureConfigsPartialUpdateApiResponse,
        FirmsCmsFeatureConfigsPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/`,
          method: 'PATCH',
          body: queryArg.patchedUpdateCmsFeatureConfigSchema,
        }),
        invalidatesTags: ['cms-feature-config'],
      }),
      firmsCmsFeatureConfigsDestroy: build.mutation<
        FirmsCmsFeatureConfigsDestroyApiResponse,
        FirmsCmsFeatureConfigsDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: ['cms-feature-config'],
      }),
      firmsCmsFeatureConfigsDetails: build.query<
        FirmsCmsFeatureConfigsDetailsApiResponse,
        FirmsCmsFeatureConfigsDetailsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/details/`,
          params: {
            run_limit: queryArg.runLimit,
          },
        }),
        providesTags: ['cms-feature-config'],
      }),
      firmsCmsFeatureConfigsTrigger: build.mutation<
        FirmsCmsFeatureConfigsTriggerApiResponse,
        FirmsCmsFeatureConfigsTriggerApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/trigger/`,
          method: 'POST',
        }),
        invalidatesTags: ['cms-feature-config'],
      }),
      firmsCmsFeatureConfigsSupportedFeatures: build.query<
        FirmsCmsFeatureConfigsSupportedFeaturesApiResponse,
        FirmsCmsFeatureConfigsSupportedFeaturesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/supported-features/`,
        }),
        providesTags: ['cms-feature-config'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type FirmsCmsFeatureConfigsListApiResponse =
  /** status 200  */ CmsFeatureConfigSchema[];
export type FirmsCmsFeatureConfigsListApiArg = {
  /** Filter by CMS type (filevine, clio, neos, etc.) */
  cmsType?:
    | 'casepeer'
    | 'clio'
    | 'filevine'
    | 'litify'
    | 'mycase'
    | 'neos'
    | 'smartadvocate';
  firmId: string;
};
export type FirmsCmsFeatureConfigsCreateApiResponse =
  /** status 201  */ CmsFeatureConfigSchema;
export type FirmsCmsFeatureConfigsCreateApiArg = {
  firmId: string;
  createCmsFeatureConfigSchema: CreateCmsFeatureConfigSchema;
};
export type FirmsCmsFeatureConfigsRetrieveApiResponse =
  /** status 200  */ CmsFeatureConfigSchema;
export type FirmsCmsFeatureConfigsRetrieveApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
};
export type FirmsCmsFeatureConfigsPartialUpdateApiResponse =
  /** status 200  */ CmsFeatureConfigSchema;
export type FirmsCmsFeatureConfigsPartialUpdateApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
  patchedUpdateCmsFeatureConfigSchema: UpdateCmsFeatureConfigSchema;
};
export type FirmsCmsFeatureConfigsDestroyApiResponse = unknown;
export type FirmsCmsFeatureConfigsDestroyApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
};
export type FirmsCmsFeatureConfigsDetailsApiResponse =
  /** status 200  */ CmsFeatureSyncFailuresSchema;
export type FirmsCmsFeatureConfigsDetailsApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
  /** How many recent bus runs to inspect (default 10, max 50) */
  runLimit?: number;
};
export type FirmsCmsFeatureConfigsTriggerApiResponse = unknown;
export type FirmsCmsFeatureConfigsTriggerApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
};
export type FirmsCmsFeatureConfigsSupportedFeaturesApiResponse =
  /** status 200  */ CmsSupportedFeaturesSchema;
export type FirmsCmsFeatureConfigsSupportedFeaturesApiArg = {
  firmId: string;
};
export type ExternalCmsType =
  | 'casepeer'
  | 'clio'
  | 'dropbox'
  | 'filevine'
  | 'mycase'
  | 'neos'
  | 'litify'
  | 'smartadvocate';
export type CmsFeature = 'task_sync' | 'document_source' | 'contact_sync';
export type ScheduleRange = {
  start: number;
  end?: number;
  step?: number;
};
export type ScheduleCalendarSpec = {
  second?: ScheduleRange[];
  minute?: ScheduleRange[];
  hour?: ScheduleRange[];
  day_of_month?: ScheduleRange[];
  month?: ScheduleRange[];
  year?: ScheduleRange[];
  day_of_week?: ScheduleRange[];
  comment?: string | null;
};
export type ScheduleIntervalSpec = {
  every: string;
  offset?: string | null;
};
export type ScheduleSpec = {
  calendars?: ScheduleCalendarSpec[];
  intervals?: ScheduleIntervalSpec[];
  cron_expressions?: string[];
  skip?: ScheduleCalendarSpec[];
  start_at?: string | null;
  end_at?: string | null;
  jitter?: string | null;
  time_zone_name?: string | null;
};
export type CmsFeatureConfigSchema = {
  id: string | string;
  firm_id: string | string;
  cms_type: ExternalCmsType;
  feature: CmsFeature;
  enabled: boolean;
  description: string;
  schedule_spec: ScheduleSpec;
  temporal_schedule_id: string | null;
  last_synced_at: string | null;
  last_sync_error: string | null;
  created_at: string;
  modified_at: string;
};
export type CreateCmsFeatureConfigSchema = {
  firm_id: string | string;
  cms_type: ExternalCmsType;
  feature: CmsFeature;
  enabled?: boolean;
  description?: string;
  schedule_spec?: ScheduleSpec;
};
export type UpdateCmsFeatureConfigSchema = {
  enabled?: boolean | null;
  description?: string | null;
  schedule_spec?: ScheduleSpec;
};
export type CmsFeatureMatterFailureSchema = {
  matter_id: string | string;
  error: string;
};
export type CmsFeatureSyncRunStatusSchema = {
  bus_run_id: string;
  bus_status: string;
  bus_started_at?: string | null;
  bus_closed_at?: string | null;
  child_workflow_id?: string | null;
  child_run_id?: string | null;
  child_workflow_type?: string | null;
  child_status?: string | null;
  feature_completed?: boolean;
  feature_failed?: boolean;
  feature_error?: string | null;
  matter_failures?: CmsFeatureMatterFailureSchema[];
};
export type CmsFeatureSyncFailuresSchema = {
  config_id: string | string;
  bus_workflow_id: string;
  cms_type: ExternalCmsType;
  feature: CmsFeature;
  runs: CmsFeatureSyncRunStatusSchema[];
};
export type CmsSupportedFeaturesSchema = {
  supported_features: {
    [key: string]: CmsFeature[];
  };
};
export const {
  useFirmsCmsFeatureConfigsListQuery,
  useFirmsCmsFeatureConfigsCreateMutation,
  useFirmsCmsFeatureConfigsRetrieveQuery,
  useFirmsCmsFeatureConfigsPartialUpdateMutation,
  useFirmsCmsFeatureConfigsDestroyMutation,
  useFirmsCmsFeatureConfigsDetailsQuery,
  useFirmsCmsFeatureConfigsTriggerMutation,
  useFirmsCmsFeatureConfigsSupportedFeaturesQuery,
} = injectedRtkApi;
